import type RootStore from "stores/RootStore";
import type {
  DevOpsOverview,
  DevOpsOverviewClientRef,
  DevOpsOverviewProject,
} from "../devops.types";

/**
 * The client attribution of ONE Azure DevOps project, read off whichever
 * `devops-overview` response is already on the rootStore.
 *
 * The boards (Pipelines, Releases, Repos, Work Items) show a "Clients" strip
 * for the project they are scoped to. They do NOT fetch the overview
 * themselves: it is the Overview page's store (`devopsOverviewStore`, Web-1)
 * or Project 360's store (`devopsProjectStore`) that holds the response, and
 * a board only reads what is there. When neither has loaded, the strip says
 * nothing rather than "no client" — an absent rollup is not evidence of an
 * unlinked project.
 *
 * Every read is defensive: the Overview store may be registered under a
 * different key than this file guesses, or not at all in a test rootStore,
 * and a wrong guess must degrade to "unknown", never to a crash.
 */
export interface ProjectClientsView {
  /**
   * True when an overview covering this connection is loaded, so an empty
   * `clients` genuinely means "not linked to a client".
   */
  known: boolean;
  /** The overview's row for the project, when it lists it. */
  project: DevOpsOverviewProject | null;
  clients: DevOpsOverviewClientRef[];
}

/** `${connectionId}:${projectId}` — the overview's project key. */
export const projectKey = (connectionId: string, projectId: string): string =>
  `${connectionId}:${projectId}`;

/** Duck-type an overview response: the shape the boards actually read. */
const isOverview = (value: unknown): value is DevOpsOverview =>
  !!value &&
  typeof value === "object" &&
  Array.isArray((value as { projects?: unknown }).projects);

interface OverviewHolder {
  overview?: unknown;
}

/**
 * Every rootStore key that may hold a `devops-overview` response: the
 * Overview page's view store (the whole picture — its registered name is the
 * coordinator's call, so both spellings are tried) and Project 360's
 * connection-scoped response. This is the TIE-BREAK order only; the loaded
 * responses are read newest first (see {@link loadedOverviews}).
 */
const OVERVIEW_HOLDER_KEYS = [
  "devopsOverviewViewStore",
  "devopsOverviewStore",
  "devopsProjectStore",
] as const;

/** `generatedAt` as a number for ordering; an unparseable stamp sorts oldest. */
const generatedStamp = (overview: DevOpsOverview): number => {
  const t = Date.parse(overview.generatedAt ?? "");
  return Number.isFinite(t) ? t : 0;
};

/**
 * Every loaded overview on the rootStore, NEWEST first (`generatedAt`
 * descending; the holder order above breaks ties). Newest, not "landing page
 * first": after Project 360 re-finds the overview for a link it just changed,
 * the landing page's older response still lists the previous attribution,
 * and the chips inside Project 360 must show what the server now holds.
 */
export function loadedOverviews(rootStore: RootStore): DevOpsOverview[] {
  const holders = rootStore as unknown as Record<string, OverviewHolder | undefined>;
  const found: DevOpsOverview[] = [];
  for (const key of OVERVIEW_HOLDER_KEYS) {
    const candidate = holders[key]?.overview;
    if (isOverview(candidate)) found.push(candidate);
  }
  // Array.prototype.sort is stable, so equal stamps keep the holder order.
  return found.sort((a, b) => generatedStamp(b) - generatedStamp(a));
}

/** Case-insensitive key match — Azure GUIDs arrive in either case. */
const sameKey = (a: string, b: string): boolean =>
  a === b || a.toLowerCase() === b.toLowerCase();

/** The overview's row for a project, or null when it does not list it. */
export function findOverviewProject(
  overview: DevOpsOverview,
  connectionId: string,
  projectId: string
): DevOpsOverviewProject | null {
  const key = projectKey(connectionId, projectId);
  return overview.projects.find((p) => sameKey(p.key, key)) ?? null;
}

/** Whether an overview covers a connection at all (else "unknown", not "none"). */
const coversConnection = (overview: DevOpsOverview, connectionId: string): boolean =>
  (Array.isArray(overview.connections) &&
    overview.connections.some((c) => sameKey(c.id, connectionId))) ||
  overview.projects.some((p) => sameKey(p.connectionId, connectionId));

/**
 * The clients linked to the scoped project, from the NEWEST loaded overview
 * that can speak about the connection at all. With no scope, or no overview
 * covering the connection, the view is `known: false` and the strip renders
 * nothing.
 *
 * The first overview that COVERS the connection is the answer, whether or not
 * it lists the project — that is the whole point of ordering them newest
 * first. An older response is not consulted to fill a gap the newest one
 * left, because "the newest rollup of this connection does not list the
 * project" is itself the current fact: its last link was removed, so it has
 * no clients. Falling through would show chips the server no longer holds —
 * the ABSENCE case of the same staleness that ordering by `generatedAt`
 * fixed for a changed value.
 */
export function clientsForProject(
  rootStore: RootStore,
  connectionId: string | null,
  projectId: string | null
): ProjectClientsView {
  if (!connectionId || !projectId) {
    return { known: false, project: null, clients: [] };
  }
  for (const overview of loadedOverviews(rootStore)) {
    // The project row is checked first: a response that lists the project is
    // covering the connection by definition, and this order tolerates a row
    // whose `key` and `connectionId` disagree.
    const project = findOverviewProject(overview, connectionId, projectId);
    if (project) {
      return { known: true, project, clients: project.clients ?? [] };
    }
    if (coversConnection(overview, connectionId)) {
      return { known: true, project: null, clients: [] };
    }
    // Says nothing about this connection — keep looking.
  }
  return { known: false, project: null, clients: [] };
}
